import { GetEnergyConsumptionResponse } from 'api/actions/get-energy-consumption/get-energy-consumption-response';
import { GetMonthlyEnergyStatsResponse } from 'api/actions/get-monthly-energy-stats/get-monthly-energy-stats-response';
import { useApi } from 'api/api-context';
import { format } from 'date-fns';
import panic from 'panic';
import { useEffect, useMemo, useState } from 'react';

type EnergyConsumptionData = GetEnergyConsumptionResponse;

/**
 * Custom hook that fetches and derives all energy consumption data for a device.
 * Encapsulates API calls, state management, and all computed values.
 *
 * @param deviceId - The Tuya device ID to fetch data for.
 */
export function useEnergyConsumptionData(deviceId: number) {
  const { getAction } = useApi();

  const [data, setData] = useState<EnergyConsumptionData | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<GetMonthlyEnergyStatsResponse | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [fixedTariff, setFixedTariff] = useState<number>(200); // €/MWh, default 200

  // ── Fetching ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!deviceId || !selectedDate) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    getAction('GetEnergyConsumption')({ parameters: { deviceId }, query: { date: dateStr } })
      .then(setData)
      .catch(panic);
  }, [deviceId, selectedDate]);

  useEffect(() => {
    if (!deviceId || !selectedDate) return;
    const monthStr = format(selectedDate, 'yyyy-MM');
    getAction('GetMonthlyEnergyStats')({ parameters: { deviceId }, query: { month: monthStr, fixedTariff } })
      .then(setMonthlyStats)
      .catch(() => setMonthlyStats(null));
  }, [deviceId, selectedDate, fixedTariff]);

  useEffect(() => {
    if (data?.device?.label) {
      document.title = `${data.device.label} - Energy Consumption`;
    }
  }, [data?.device?.label]);

  // ── Derived values ──────────────────────────────────────────────────────────

  const spotPrices = useMemo(() => data?.spotPrices ?? [], [data]);

  const totalConsumption = useMemo(
    () =>
      data ? data.intervals.filter((i) => i.consumption !== null).reduce((sum, i) => sum + (i.consumption || 0), 0) : 0,
    [data]
  );

  /** Labels for intervals that have non-null consumption */
  const validIntervalLabels = useMemo(
    () => (data ? data.intervals.filter((i) => i.consumption !== null).map((i) => i.label) : []),
    [data]
  );

  /** Cost per interval at spot prices (€), null if no match found */
  const costData = useMemo(() => {
    if (!data || spotPrices.length === 0) return null;

    return data.intervals.map((interval) => {
      if (interval.consumption === null) return null;

      const [hours, minutes] = interval.label!.split(':').map(Number);
      const intervalStart = new Date(
        Date.UTC(
          new Date(data.date).getUTCFullYear(),
          new Date(data.date).getUTCMonth(),
          new Date(data.date).getUTCDate(),
          hours,
          minutes,
          0,
          0
        )
      );

      const match = spotPrices.find((sp) => {
        const s = new Date(sp.deliveryStart!);
        const e = new Date(sp.deliveryEnd!);
        return intervalStart >= s && intervalStart < e;
      });

      if (!match?.price) return null;
      // Wh × (€/MWh) / 1_000_000 = €
      return (interval.consumption! * match.price) / 1_000_000;
    });
  }, [data, spotPrices]);

  const totalCost = useMemo(
    () => (costData ? costData.filter((c): c is number => c !== null).reduce((s, c) => s + c, 0) : null),
    [costData]
  );

  /** Cost per interval at fixed tariff (€/MWh) */
  const fixedTariffCostData = useMemo(
    () =>
      data
        ? data.intervals.map((i) => (i.consumption === null ? null : (i.consumption! * fixedTariff) / 1_000_000))
        : null,
    [data, fixedTariff]
  );

  const totalFixedTariffCost = useMemo(
    () =>
      fixedTariffCostData
        ? fixedTariffCostData.filter((c): c is number => c !== null).reduce((s, c) => s + c, 0)
        : null,
    [fixedTariffCostData]
  );

  /** Spot cost minus fixed cost per interval */
  const differenceData = useMemo(() => {
    if (!costData || !fixedTariffCostData) return null;
    return costData.map((spotCost, i) => {
      const fixedCost = fixedTariffCostData[i];
      if (spotCost === null || fixedCost === null) return null;
      return spotCost - fixedCost;
    });
  }, [costData, fixedTariffCostData]);

  /** Shared Y-axis maximum for spot and fixed cost charts (with 10% padding) */
  const sharedCostYMax = useMemo(() => {
    if (!costData || !fixedTariffCostData) return undefined;
    const validSpot = costData.filter((c): c is number => c !== null);
    const validFixed = fixedTariffCostData.filter((c): c is number => c !== null);
    if (!validSpot.length && !validFixed.length) return undefined;
    return (
      Math.max(validSpot.length ? Math.max(...validSpot) : 0, validFixed.length ? Math.max(...validFixed) : 0) * 1.1
    );
  }, [costData, fixedTariffCostData]);

  return {
    data,
    monthlyStats,
    selectedDate,
    setSelectedDate,
    fixedTariff,
    setFixedTariff,
    spotPrices,
    totalConsumption,
    validIntervalLabels,
    costData,
    totalCost,
    fixedTariffCostData,
    totalFixedTariffCost,
    differenceData,
    sharedCostYMax,
  };
}
